import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import { FINISHER_KINDS } from "./constants";

// Inline finisher panel — shown after a progression scheme is applied and via
// the standalone MesocycleFinisherDialog entry point.
export function MesocycleFinisherPanel({
  programId, sourceDay, sourceSection, exerciseName, sessionType, initialBase1rm,
  onCancel, onApplied,
}) {
  const [kind, setKind] = useState("deload");
  const [base1rm, setBase1rm] = useState(initialBase1rm || 100);
  const [preview, setPreview] = useState(null);
  const [targetWeek, setTargetWeek] = useState(null);
  const [alreadyHas, setAlreadyHas] = useState(false);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/coach/programs/${programId}/finisher/preview`, {
        kind, base_1rm: Number(base1rm),
      });
      setPreview(data.prescription);
      setTargetWeek(data.target_week);
      setAlreadyHas(!!data.already_has_finisher);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally { setLoading(false); }
  };
  React.useEffect(() => { load(); }, [kind, base1rm]);

  const apply = async () => {
    if (!exerciseName?.trim()) return toast.error("Exercise name required");
    setApplying(true);
    try {
      await api.post(`/coach/programs/${programId}/finisher/apply`, {
        kind, base_1rm: Number(base1rm),
        source_day: sourceDay, source_section: sourceSection,
        exercise_name: exerciseName, session_type: sessionType,
      });
      toast.success(`${kind === "deload" ? "Deload" : "Peak"} week appended`);
      onApplied?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed");
    } finally { setApplying(false); }
  };

  const removeFinisher = async () => {
    setApplying(true);
    try {
      await api.delete(`/coach/programs/${programId}/finisher`);
      toast.success("Finisher week removed");
      setAlreadyHas(false);
      load();
      onApplied?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    } finally { setApplying(false); }
  };

  return (
    <div className="mt-3 border !border-yellow-400/40 bg-surface-elevated/60 p-4 space-y-3" data-testid="finisher-panel">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-tiny !text-yellow-400">// MESOCYCLE FINISHER</div>
          <div className="font-heading text-lg text-white">Cap the Block</div>
        </div>
        <button
          onClick={onCancel}
          className="text-[11px] label-tiny tracking-widest text-white/60 hover:text-white"
          data-testid="finisher-panel-cancel"
        >
          BACK
        </button>
      </div>

      {alreadyHas && (
        <div className="border !border-yellow-400/50 bg-yellow-400/10 p-2 text-xs text-white/90 flex items-center justify-between gap-2" data-testid="finisher-already-notice">
          <span>This program already has a finisher week. Remove it to add another.</span>
          <button
            onClick={removeFinisher}
            disabled={applying}
            className="text-[11px] label-tiny tracking-widest border border-white/30 text-white hover:!border-red-400 hover:!text-red-400 px-2 py-1 disabled:opacity-50"
            data-testid="finisher-remove-button"
          >
            REMOVE
          </button>
        </div>
      )}

      <div>
        <label className="label-tiny text-[11px] text-white/70 block mb-2">CHOOSE FINISHER</label>
        <div className="grid grid-cols-2 gap-2" data-testid="finisher-kind-picker">
          {FINISHER_KINDS.map((k) => (
            <button
              key={k.id}
              onClick={() => setKind(k.id)}
              data-testid={`finisher-kind-${k.id}`}
              className={`text-left p-3 border transition-colors ${kind === k.id ? "!bg-yellow-400 !text-slate-900 !border-yellow-400 shadow-lg shadow-yellow-400/30" : "border-white/25 text-white/85 hover:!border-yellow-400 hover:!text-yellow-400 bg-surface-elevated/60"}`}
            >
              <div className="font-heading text-base uppercase tracking-wider">{k.label}</div>
              <div className={`text-[11px] mt-0.5 ${kind === k.id ? "text-slate-800" : "text-white/60"}`}>{k.hint}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-1">
          <label className="label-tiny text-[11px] text-white/70 block mb-1.5">BASE 1RM (KG)</label>
          <Input
            type="number" min="1" value={base1rm}
            onChange={(e) => setBase1rm(e.target.value)}
            data-testid="finisher-base-1rm"
            className="bg-surface-elevated border-white/25 text-white text-sm h-9"
          />
        </div>
        <div className="col-span-2">
          <label className="label-tiny text-[11px] text-white/70 block mb-1.5">EXERCISE</label>
          <div className="h-9 flex items-center px-3 border border-white/20 bg-surface-base/60 text-sm text-white/90 truncate" data-testid="finisher-exercise-label">
            {exerciseName || "—"} · <span className="!text-yellow-400 ml-1">{(sourceSection || "").toUpperCase()}</span> · Day {sourceDay}
          </div>
        </div>
      </div>

      <div>
        <label className="label-tiny text-[11px] text-white/70 block mb-1.5">
          FINAL WEEK PREVIEW{targetWeek ? ` — WEEK ${targetWeek}` : ""}
        </label>
        <div className="border border-white/20 bg-surface-base/40" data-testid="finisher-preview-grid">
          <div className="grid grid-cols-5 text-[11px] label-tiny text-white/70 border-b-2 !border-yellow-400/50 bg-surface-elevated font-bold">
            <div className="px-3 py-2">SETS</div>
            <div className="px-3 py-2">REPS</div>
            <div className="px-3 py-2">%1RM</div>
            <div className="px-3 py-2">KG</div>
            <div className="px-3 py-2">RPE</div>
          </div>
          {loading ? (
            <div className="p-4 text-xs text-white/60">Computing…</div>
          ) : !preview ? (
            <div className="p-4 text-xs text-white/60">No preview.</div>
          ) : (
            <>
              <div className="grid grid-cols-5 text-sm border-b border-white/10 tabular-nums items-center" data-testid="finisher-preview-row">
                <div className="px-3 py-2 text-white">{preview.sets}</div>
                <div className="px-3 py-2 text-white">{preview.reps}</div>
                <div className="px-3 py-2 text-white/90">{preview.percentage_1rm}%</div>
                <div className="px-3 py-2 !text-yellow-400 font-heading">{preview.weight_kg}</div>
                <div className="px-3 py-2 text-white/90">{preview.rpe}</div>
              </div>
              <div className="px-3 py-2 text-[11px] text-white/70 italic">{preview.notes}</div>
            </>
          )}
        </div>
      </div>

      <button
        onClick={apply}
        disabled={applying || loading || alreadyHas || !preview}
        className="!bg-yellow-400 hover:bg-yellow-300 text-slate-900 font-bold transition-colors w-full py-3.5 text-sm label-tiny tracking-widest disabled:opacity-50"
        data-testid="finisher-apply-button"
      >
        {applying ? "APPLYING..."
          : loading ? "LOADING…"
          : alreadyHas ? "FINISHER ALREADY APPLIED"
          : `APPEND ${kind.toUpperCase()} WEEK${targetWeek ? ` ${targetWeek}` : ""}`}
      </button>
    </div>
  );
}

// Standalone dialog wrapper — opened from the ProgramEditor header button.
export function MesocycleFinisherDialog({ programId, defaultSourceDay, defaultSourceSection, defaultExerciseName, defaultSessionType, defaultBase1rm, onClose, onApplied }) {
  const [source, setSource] = useState({
    sourceDay: defaultSourceDay || 1,
    sourceSection: defaultSourceSection || "strength",
    exerciseName: defaultExerciseName || "",
    sessionType: defaultSessionType || "strength",
  });
  const [configured, setConfigured] = useState(!!(defaultExerciseName || "").trim());
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-surface-DEFAULT border-white/20 text-white max-w-2xl" data-testid="finisher-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-white">MESOCYCLE FINISHER</DialogTitle>
          <DialogDescription className="text-xs text-white/70">
            Cap this mesocycle with a deload or peak week. Applied to a single anchor lift.
          </DialogDescription>
        </DialogHeader>
        {!configured ? (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="label-tiny text-[11px] text-white/70 block mb-1.5">EXERCISE NAME</label>
                <Input
                  value={source.exerciseName}
                  onChange={(e) => setSource((s) => ({ ...s, exerciseName: e.target.value }))}
                  placeholder="e.g. Back Squat"
                  data-testid="finisher-dialog-exercise"
                  className="bg-surface-elevated border-white/25 text-white text-sm h-9"
                />
              </div>
              <div>
                <label className="label-tiny text-[11px] text-white/70 block mb-1.5">SECTION</label>
                <Input
                  value={source.sourceSection}
                  onChange={(e) => setSource((s) => ({ ...s, sourceSection: e.target.value }))}
                  placeholder="strength"
                  data-testid="finisher-dialog-section"
                  className="bg-surface-elevated border-white/25 text-white text-sm h-9"
                />
              </div>
              <div>
                <label className="label-tiny text-[11px] text-white/70 block mb-1.5">DAY (1-7)</label>
                <Input
                  type="number" min="1" max="7"
                  value={source.sourceDay}
                  onChange={(e) => setSource((s) => ({ ...s, sourceDay: Number(e.target.value) || 1 }))}
                  data-testid="finisher-dialog-day"
                  className="bg-surface-elevated border-white/25 text-white text-sm h-9"
                />
              </div>
            </div>
            <div className="text-[11px] text-white/60 italic">Enter the anchor lift and its slot, then continue.</div>
            <button
              onClick={() => setConfigured(true)}
              disabled={!source.exerciseName.trim()}
              className="!bg-yellow-400 hover:bg-yellow-300 text-slate-900 font-bold transition-colors w-full py-3 text-xs label-tiny tracking-widest disabled:opacity-50"
              data-testid="finisher-dialog-continue"
            >
              CONTINUE
            </button>
          </div>
        ) : (
          <MesocycleFinisherPanel
            programId={programId}
            sourceDay={source.sourceDay}
            sourceSection={source.sourceSection}
            exerciseName={source.exerciseName}
            sessionType={source.sessionType}
            initialBase1rm={defaultBase1rm || 100}
            onCancel={onClose}
            onApplied={() => { onApplied?.(); onClose(); }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
